import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import CommandBar from '../../components/CommandBar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Link2,
  Search,
  Copy,
  Check,
  Download,
  ArrowLeft,
  Filter,
  Loader2,
  ShieldCheck,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CopyButton({ text, label }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 1500);
  };
  if (!text) return <span className="text-xs text-[hsl(215,20%,35%)]">—</span>;
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center space-x-1.5 group max-w-full"
      data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <span className="text-xs text-[hsl(215,20%,65%)] font-mono truncate max-w-[260px] group-hover:text-blue-400 transition-colors">
        {text}
      </span>
      {copied ? (
        <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
      ) : (
        <Copy className="w-3.5 h-3.5 text-[hsl(215,20%,40%)] group-hover:text-blue-400 flex-shrink-0 transition-colors" />
      )}
    </button>
  );
}

const STATUS_STYLES = {
  active: 'bg-green-500/15 text-green-400 border-green-500/30',
  inactive: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
  unassigned: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  disabled: 'bg-red-500/15 text-red-400 border-red-500/30',
};

const CATEGORY_STYLES = {
  'Review Collection': 'bg-blue-500/15 text-blue-400',
  'Guest Survey': 'bg-emerald-500/15 text-emerald-400',
  'Retailer Alert': 'bg-fuchsia-500/15 text-fuchsia-400',
  'Restroom Survey': 'bg-cyan-500/15 text-cyan-400',
};

export default function URLRegistry() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const isSuperAdmin = user?.role === 'super_admin';

  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [stores, setStores] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [storeFilter, setStoreFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [ownerAccessDialog, setOwnerAccessDialog] = useState(false);
  const [ownerAccessOrgs, setOwnerAccessOrgs] = useState([]);
  const [ownerAccessLoading, setOwnerAccessLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchRegistry(); }, []);

  const fetchRegistry = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/url-registry`, { headers });
      if (!res.ok) {
        const d = await res.json();
        toast.error(d.detail || 'Failed to load registry');
        setLoading(false);
        return;
      }
      const data = await res.json();
      setEntries(data.entries || []);
      setStores(data.stores || []);
    } catch {
      toast.error('Connection error');
    }
    setLoading(false);
  };

  // Client-side filtering for instant feedback
  const filtered = useMemo(() => {
    let list = entries;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      list = list.filter(e =>
        e.store_name.toLowerCase().includes(s) ||
        e.location_name.toLowerCase().includes(s) ||
        e.tag_id.toLowerCase().includes(s) ||
        e.tap_url.toLowerCase().includes(s) ||
        e.landing_url.toLowerCase().includes(s)
      );
    }
    if (storeFilter) list = list.filter(e => e.organization_id === storeFilter);
    if (statusFilter) list = list.filter(e => e.status === statusFilter);
    if (categoryFilter) list = list.filter(e => e.category === categoryFilter);
    return list;
  }, [entries, searchTerm, storeFilter, statusFilter, categoryFilter]);

  const categories = useMemo(() => [...new Set(entries.map(e => e.category))].sort(), [entries]);
  const statuses = useMemo(() => [...new Set(entries.map(e => e.status))].sort(), [entries]);

  const handleExport = () => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (storeFilter) params.set('store', storeFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (categoryFilter) params.set('category', categoryFilter);
    window.open(`${API_URL}/api/url-registry/export?${params.toString()}`, '_blank');
  };

  // Owner access dialog
  const loadOwnerAccess = async () => {
    setOwnerAccessLoading(true);
    try {
      const orgs = [];
      for (const s of stores) {
        const res = await fetch(`${API_URL}/api/url-registry/owner-access/${s.id}`, { headers });
        if (res.ok) {
          const d = await res.json();
          orgs.push(d);
        }
      }
      setOwnerAccessOrgs(orgs);
    } catch { /* silent */ }
    setOwnerAccessLoading(false);
  };

  const toggleOwnerAccess = async (orgId, currentValue) => {
    try {
      const res = await fetch(`${API_URL}/api/url-registry/owner-access/${orgId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url_registry_access: !currentValue }),
      });
      if (res.ok) {
        setOwnerAccessOrgs(prev =>
          prev.map(o => o.organization_id === orgId ? { ...o, url_registry_access: !currentValue } : o)
        );
        toast.success(`Access ${!currentValue ? 'granted' : 'revoked'}`);
      }
    } catch { toast.error('Update failed'); }
  };

  const baseUrl = API_URL.replace('/api', '').replace(/\/$/, '');

  return (
    <div className="min-h-screen bg-background" data-testid="url-registry-page">
      <CommandBar
        title="URL Registry"
        subtitle="Tag Routing Source of Truth"
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/hotels')}
              data-testid="back-to-properties-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Properties
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              data-testid="export-csv-btn"
              className="border-green-500/30 text-green-400 hover:bg-green-500/10"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            {isSuperAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setOwnerAccessDialog(true); loadOwnerAccess(); }}
                data-testid="owner-access-btn"
                className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
              >
                <ShieldCheck className="w-4 h-4 mr-2" />
                Owner Access
              </Button>
            )}
          </>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
        {/* Filters */}
        <Card className="bg-card border-border" data-testid="url-registry-filters">
          <CardContent className="py-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search store, location, tag ID, or URL..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-10 bg-background border-border"
                  data-testid="registry-search-input"
                />
              </div>
              {isSuperAdmin && stores.length > 0 && (
                <Select value={storeFilter} onValueChange={v => setStoreFilter(v === '__all__' ? '' : v)}>
                  <SelectTrigger className="w-[200px] bg-background border-border" data-testid="filter-store">
                    <Filter className="w-3.5 h-3.5 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="All Stores" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Stores</SelectItem>
                    {stores.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={statusFilter} onValueChange={v => setStatusFilter(v === '__all__' ? '' : v)}>
                <SelectTrigger className="w-[150px] bg-background border-border" data-testid="filter-status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Status</SelectItem>
                  {statuses.map(s => (
                    <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={v => setCategoryFilter(v === '__all__' ? '' : v)}>
                <SelectTrigger className="w-[180px] bg-background border-border" data-testid="filter-category">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Categories</SelectItem>
                  {categories.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(searchTerm || storeFilter || statusFilter || categoryFilter) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setSearchTerm(''); setStoreFilter(''); setStatusFilter(''); setCategoryFilter(''); }}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="clear-filters-btn"
                >
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing <span className="font-medium text-foreground">{filtered.length}</span> of {entries.length} entries
          </p>
          <div className="flex items-center space-x-2">
            {categories.map(c => {
              const count = filtered.filter(e => e.category === c).length;
              if (!count) return null;
              return (
                <span key={c} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${CATEGORY_STYLES[c] || 'bg-gray-500/15 text-gray-400'}`}>
                  {c}: {count}
                </span>
              );
            })}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
          </div>
        ) : (
          <Card className="bg-card border-border overflow-hidden" data-testid="url-registry-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Store</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Location</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tag ID</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Category</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tap URL</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Landing Page URL</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Created</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-12 text-center text-sm text-muted-foreground">
                        {entries.length === 0 ? 'No tags found in the system.' : 'No results match your filters.'}
                      </td>
                    </tr>
                  ) : (
                    filtered.map((entry, i) => (
                      <tr key={`${entry.tag_id}-${i}`} className="hover:bg-muted/20 transition-colors" data-testid={`registry-row-${entry.tag_id}`}>
                        <td className="px-4 py-3 text-sm text-foreground font-medium whitespace-nowrap">{entry.store_name || '—'}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{entry.location_name || '—'}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs font-mono font-bold text-foreground bg-muted/40 px-2 py-0.5 rounded">{entry.tag_id}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${CATEGORY_STYLES[entry.category] || 'bg-gray-500/15 text-gray-400'}`}>
                            {entry.category}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <CopyButton text={entry.tap_url ? `${baseUrl}${entry.tap_url}` : ''} label="Tap URL" />
                        </td>
                        <td className="px-4 py-3">
                          <CopyButton text={entry.landing_url ? `${baseUrl}${entry.landing_url}` : ''} label="Landing URL" />
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[entry.status] || STATUS_STYLES.inactive}`}>
                            {entry.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                          {entry.created_at ? entry.created_at.slice(0, 10) : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                          {entry.updated_at ? entry.updated_at.slice(0, 10) : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Owner Access Dialog */}
      <Dialog open={ownerAccessDialog} onOpenChange={setOwnerAccessDialog}>
        <DialogContent className="bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)] text-white max-w-lg max-h-[80vh] overflow-y-auto" data-testid="owner-access-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center space-x-2">
              <ShieldCheck className="w-5 h-5 text-amber-400" />
              <span>Owner URL Access Control</span>
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[hsl(215,20%,55%)] mb-4">
            Control which organizations can view the URL Registry. When enabled, all Client Admin users within
            that organization can access the registry and view only their own URLs.
          </p>
          {ownerAccessLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
            </div>
          ) : (
            <div className="space-y-2">
              {ownerAccessOrgs.map(org => (
                <div
                  key={org.organization_id}
                  className="flex items-center justify-between p-3 bg-[hsl(217,19%,15%)] rounded-lg"
                  data-testid={`owner-access-${org.organization_id}`}
                >
                  <div className="min-w-0 flex-1 mr-3">
                    <p className="text-sm font-medium text-white truncate">{org.organization_name}</p>
                    <p className="text-[10px] text-[hsl(215,20%,45%)] font-mono truncate">{org.organization_id}</p>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <span className={`text-[10px] font-medium ${org.url_registry_access ? 'text-green-400' : 'text-gray-500'}`}>
                      {org.url_registry_access ? 'ON' : 'OFF'}
                    </span>
                    <Switch
                      checked={org.url_registry_access}
                      onCheckedChange={() => toggleOwnerAccess(org.organization_id, org.url_registry_access)}
                      data-testid={`toggle-access-${org.organization_id}`}
                    />
                  </div>
                </div>
              ))}
              {ownerAccessOrgs.length === 0 && (
                <p className="text-center py-4 text-xs text-[hsl(215,20%,45%)]">No organizations found.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
